/**
 * Member Work Compliance Dashboard
 * Displays work requirement compliance status, submission history, and earned incentives
 * Trust Marketplace - Work Documentation System
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Progress } from '../ui/progress';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import {
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  Upload,
  FileText,
  DollarSign,
  Star,
  TrendingUp,
  Award
} from 'lucide-react';

interface WorkComplianceDashboardProps {
  memberId: number;
}

interface ComplianceData {
  enrollment_id: number;
  member_id: number;
  current_month: number;
  current_year: number;
  current_month_hours: number;
  current_month_compliant: boolean;
  current_month_status: string;
  deadline_date: string;
  days_until_deadline: number;
  hours_needed: number;
  last_6_months_compliance: Array<{
    month: number;
    year: number;
    total_hours: number;
    compliant: boolean;
    verification_status: string;
    submitted_date?: string;
  }>;
  consecutive_compliant_months: number;
  active_alerts: Array<{
    id: number;
    type: string;
    severity: string;
    title: string;
    message: string;
    deadline_date?: string;
  }>;
  pending_verifications: number;
  total_points_earned: number;
  total_incentives_earned: number;
  total_incentives_paid: number;
  incentives_pending: number;
}

const MemberWorkComplianceDashboard: React.FC<WorkComplianceDashboardProps> = ({ memberId }) => {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const [data, setData] = useState<ComplianceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchComplianceData();
  }, [memberId]);

  const fetchComplianceData = async () => {
    try {
      setLoading(true);
      const url = getWeaveinApiUrl(`/api/work-compliance/member/${memberId}/dashboard`);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch compliance data: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching compliance data:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (month: number) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'VERIFIED':
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'NOT_SUBMITTED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="p-6">
          <div className="flex items-center text-red-600">
            <AlertCircle className="mr-2" />
            <span>{error || 'No compliance data found'}</span>
          </div>
        </Card>
      </div>
    );
  }

  const progressPercentage = Math.min((data.current_month_hours / 80) * 100, 100);
  const urgencyColor = data.days_until_deadline <= 7 ? 'text-red-600' : 'text-gray-600';

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        {!tabbed && <h1 className="text-3xl font-bold">{t('workcompliance.work_requirement_compliance', 'Work Requirement Compliance')}</h1>}
        <button
          onClick={() => window.location.href = '/work-compliance/submit'}
          className="ml-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Upload size={20} />
          {t('workcompliance.submit_work_hours', 'Submit Work Hours')}
        </button>
      </div>

      {/* Active Alerts */}
      {data.active_alerts.length > 0 && (
        <Card className="p-4 border-l-4 border-yellow-500">
          {data.active_alerts.map(alert => (
            <div key={alert.id} className="flex items-start gap-3 mb-2 last:mb-0">
              <AlertCircle className="text-yellow-600 mt-1" size={20} />
              <div className="flex-1">
                <p className="font-semibold">{alert.title}</p>
                <p className="text-sm text-gray-600">{alert.message}</p>
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Current Month Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Progress Card */}
        <Card className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-lg font-semibold">
                {getMonthName(data.current_month)} {data.current_year} Compliance
              </h2>
              <p className="text-sm text-gray-600">{t('workcompliance.required_80_hours_per_month', 'Required: 80 hours per month')}</p>
            </div>
            <div className={`flex items-center gap-1 ${urgencyColor}`}>
              <Clock size={16} />
              <span className="text-sm font-medium">{data.days_until_deadline} days left</span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('workcompliance.hours_submitted', 'Hours Submitted')}</span>
              <span className="font-bold">{data.current_month_hours} / 80</span>
            </div>

            <Progress value={progressPercentage} className="h-3" />

            <div className="flex items-center justify-between pt-2">
              <Badge className={getStatusColor(data.current_month_status)}>
                {data.current_month_status.replace('_', ' ')}
              </Badge>

              {data.current_month_compliant ? (
                <div className="flex items-center text-green-600 gap-1">
                  <CheckCircle2 size={20} />
                  <span className="font-semibold">{t('workcompliance.compliant', 'Compliant')}</span>
                </div>
              ) : (
                <div className="flex items-center text-orange-600 gap-1">
                  <AlertCircle size={20} />
                  <span className="font-semibold">{data.hours_needed} hours needed</span>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Incentives Earned Card */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Award className="text-yellow-500" />
            {t('workcompliance.earned_incentives', 'Earned Incentives')}
          </h2>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Star className="text-yellow-500" size={20} />
                <span className="text-gray-600">{t('workcompliance.points_earned', 'Points Earned')}</span>
              </div>
              <span className="text-2xl font-bold">{data.total_points_earned}</span>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <DollarSign className="text-green-500" size={20} />
                <span className="text-gray-600">{t('workcompliance.total_earned', 'Total Earned')}</span>
              </div>
              <span className="text-2xl font-bold">${data.total_incentives_earned.toFixed(2)}</span>
            </div>

            <div className="border-t pt-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('workcompliance.paid', 'Paid')}</span>
                <span className="text-green-600 font-semibold">${data.total_incentives_paid.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('workcompliance.pending', 'Pending')}</span>
                <span className="text-yellow-600 font-semibold">${data.incentives_pending.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Consecutive Months Streak */}
      {data.consecutive_compliant_months > 0 && (
        <Card className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingUp className="text-blue-600" size={24} />
              <div>
                <p className="font-semibold text-lg">
                  {data.consecutive_compliant_months} Month Streak! 🔥
                </p>
                <p className="text-sm text-gray-600">
                  {t('workcompliance.keep_it_up_youre_on', 'Keep it up! You\'re on track for quarterly/annual bonuses.')}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* 6-Month Compliance History */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Calendar />
          6-Month Compliance History
        </h2>

        <div className="grid grid-cols-6 gap-3">
          {data.last_6_months_compliance.map((month, index) => (
            <div
              key={index}
              className={`p-3 rounded-lg border ${
                month.compliant
                  ? 'bg-green-50 border-green-200'
                  : month.verification_status === 'PENDING'
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="text-center">
                <p className="text-xs font-semibold text-gray-600">
                  {getMonthName(month.month)} '{month.year.toString().slice(-2)}
                </p>
                <p className="text-2xl font-bold mt-1">{month.total_hours}</p>
                <p className="text-xs text-gray-500">hrs</p>
                {month.compliant ? (
                  <CheckCircle2 className="mx-auto mt-2 text-green-600" size={16} />
                ) : (
                  <Badge className={`text-xs mt-2 ${getStatusColor(month.verification_status)}`}>
                    {month.verification_status || 'N/A'}
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Pending Verifications */}
      {data.pending_verifications > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 text-yellow-600">
            <FileText size={20} />
            <span className="font-semibold">
              {data.pending_verifications} submission(s) pending verification by CBO
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1 ml-7">
            {t('workcompliance.your_cbo_will_review_within', 'Your CBO will review within 3 business days. You\'ll be notified when complete.')}
          </p>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">{t('workcompliance.quick_actions', 'Quick Actions')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => window.location.href = '/work-compliance/submit'}
            className="p-4 border-2 border-blue-200 rounded-lg hover:bg-blue-50 transition text-left"
          >
            <Upload className="text-blue-600 mb-2" size={24} />
            <p className="font-semibold">{t('workcompliance.submit_work_hours_1', 'Submit Work Hours')}</p>
            <p className="text-sm text-gray-600">{t('workcompliance.for_current_month', 'For current month')}</p>
          </button>

          <button
            onClick={() => window.location.href = '/work-compliance/documents'}
            className="p-4 border-2 border-purple-200 rounded-lg hover:bg-purple-50 transition text-left"
          >
            <FileText className="text-purple-600 mb-2" size={24} />
            <p className="font-semibold">{t('workcompliance.upload_documents', 'Upload Documents')}</p>
            <p className="text-sm text-gray-600">{t('workcompliance.pay_stubs_timesheets', 'Pay stubs, timesheets')}</p>
          </button>

          <button
            onClick={() => window.location.href = '/work-compliance/history'}
            className="p-4 border-2 border-green-200 rounded-lg hover:bg-green-50 transition text-left"
          >
            <Calendar className="text-green-600 mb-2" size={24} />
            <p className="font-semibold">{t('workcompliance.view_full_history', 'View Full History')}</p>
            <p className="text-sm text-gray-600">{t('workcompliance.all_submissions', 'All submissions')}</p>
          </button>
        </div>
      </Card>
    </div>
  );
};

export default MemberWorkComplianceDashboard;
